<script setup>
import { ref, watch, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'

/**
 * Collapse — drop-in replacement for BV2's b-collapse.
 *
 * Props:
 *   id        — string, used by v-collapse-toggle to target this collapse
 *   visible   — boolean, initial visibility (static)
 *   value     — boolean, v-model binding (runtime control)
 *   accordion — string, accordion group id (only one open at a time)
 *
 * Events:
 *   input     — emitted when visibility changes (for v-model)
 *   show      — emitted when collapse opens
 *   hide      — emitted when collapse closes
 */
const props = defineProps({
    id: { type: String },
    visible: { type: Boolean, default: false },
    value: { type: Boolean, default: undefined },
    accordion: { type: String },
})

const emit = defineEmits(['input', 'show', 'hide'])

const isVisible = ref(props.value !== undefined ? props.value : props.visible)
const collapseEl = ref(null)
const vm = getCurrentInstance().proxy

function emitState(visible) {
    if (props.id) vm.$root.$emit('collapse::state', props.id, visible)
}

function animateOpen(el) {
    el.style.display = 'block'
    el.style.height = '0'
    el.style.overflow = 'hidden'
    void el.offsetHeight
    el.style.transition = 'height 0.35s ease'
    el.style.height = el.scrollHeight + 'px'
    el.addEventListener('transitionend', () => {
        el.style.height = ''
        el.style.overflow = ''
        el.style.transition = ''
    }, { once: true })
}

function animateClose(el) {
    el.style.display = 'block'  // prevent Bootstrap .collapse:not(.show){display:none} from interfering
    el.style.height = el.scrollHeight + 'px'
    el.style.overflow = 'hidden'
    void el.offsetHeight
    el.style.transition = 'height 0.35s ease'
    el.style.height = '0'
    el.addEventListener('transitionend', () => {
        el.style.height = ''
        el.style.overflow = ''
        el.style.transition = ''
        el.style.display = 'none'
    }, { once: true })
}

// Core: animate and update all state — never emits accordion event
function _animate(visible) {
    if (visible === isVisible.value) return
    isVisible.value = visible
    if (collapseEl.value) {
        if (visible) animateOpen(collapseEl.value)
        else animateClose(collapseEl.value)
    }
    emitState(visible)
    emit('input', visible)
    emit(visible ? 'show' : 'hide')
}

function show() {
    if (props.accordion) {
        // Emit accordion event — onRootAccordion handles the actual animation
        vm.$root.$emit('collapse::accordion', props.accordion, props.id)
        return
    }
    _animate(true)
}

function hide() {
    _animate(false)
}

function toggle() {
    isVisible.value ? hide() : show()
}

function onRootToggle(id) {
    if (id === props.id) toggle()
}

function onRootAccordion(accordion, id) {
    if (accordion === props.accordion) {
        _animate(id === props.id)
    }
}

watch(() => props.value, val => {
    if (val !== undefined) _animate(val)
})

onMounted(() => {
    if (!isVisible.value && collapseEl.value) {
        collapseEl.value.style.display = 'none'
    }
    vm.$root.$on('collapse::toggle', onRootToggle)
    if (props.accordion) {
        vm.$root.$on('collapse::accordion', onRootAccordion)
    }
    emitState(isVisible.value)
})

onBeforeUnmount(() => {
    vm.$root.$off('collapse::toggle', onRootToggle)
    if (props.accordion) {
        vm.$root.$off('collapse::accordion', onRootAccordion)
    }
})
</script>

<template>
    <div ref="collapseEl" :id="id" class="collapse" :class="{ show: isVisible }">
        <slot />
    </div>
</template>
